import type { Car } from '@ahd/shared/types/ahd-cars/car/car';
import type { FirmaBilderSettings } from '@ahd/shared/types/ahd-cars/firma/bilder-settings';
import { combineReducers, createStore } from 'redux';
import { type AxiosGetCarlist } from '../_tools/AxiosCar';
import { type Search, type Sort } from '../_tools/FilterSidebar';
import { type Testphasen, type Vertrage } from '../_tools/Vertrag';
import { type UserSettings } from '../login/EnsureLoggedInContainer';
import {
    CLOSE_SNACKBAR,
    ENQUEUE_SNACKBAR,
    REMOVE_SNACKBAR,
    SET_ALLVEHICLESSTATE,
    SET_CARLIST,
    SET_CARLIST_FILTER,
    SET_CARLIST_FILTERED,
    SET_CARLIST_SIZE,
    SET_CHANGED_CARS,
    SET_IMAGESETTINGS,
    SET_LAST_LEAD_LIST_UPDATE,
    SET_LEADSTATUS,
    SET_LOGIN_TOKEN,
    SET_TESTPHASE,
    SET_UNCHANGED_DATA,
    SET_UPDATE_REQUIRED,
    SET_USER_SETTINGS,
    SET_VERTRAGE,
} from './Actions';

function toastReducer(state: FluxStore['app'] = { notifications: [] }, action: any = {}) {
    switch (action.type) {
        case ENQUEUE_SNACKBAR:
            return {
                ...state,
                notifications: [
                    ...state.notifications,
                    {
                        key: action.key,
                        ...action.notification,
                    },
                ],
            };

        case CLOSE_SNACKBAR:
            return {
                ...state,
                notifications: state.notifications.map((notification) => (action.dismissAll || notification.key === action.key ? { ...notification, dismissed: true } : { ...notification })),
            };

        case REMOVE_SNACKBAR:
            return {
                ...state,
                notifications: state.notifications.filter((notification) => notification.key !== action.key),
            };

        default:
            return state;
    }
}

function allVehicleStateReducer(allVehiclesState: FluxStore['allVehiclesState'] = {}, action: any = {}) {
    if (action.type === SET_ALLVEHICLESSTATE) {
        return action.allVehiclesState;
    } else {
        return allVehiclesState;
    }
}

function imageSettingsReducer(imageSettings: Partial<FluxStore['imageSettings']> = {}, action: any = {}) {
    if (action.type === SET_IMAGESETTINGS) {
        return action.imageSettings;
    } else {
        return imageSettings;
    }
}

function leadstatusReducer(state: FluxStore['leadstatus'] = 0, action: any = {}) {
    if (action.type === SET_LEADSTATUS) {
        return action.leadstatus;
    } else {
        return state;
    }
}

function lastLeadListUpdate(state: FluxStore['lastLeadListUpdate'] = 0, action: any = {}) {
    if (action.type === SET_LAST_LEAD_LIST_UPDATE) {
        return action.lastLeadListUpdate;
    } else {
        return state;
    }
}

function updateRequiredReducer(state: FluxStore['updateRequired'] = null, action: any = {}) {
    if (action.type === SET_UPDATE_REQUIRED) {
        return action.state;
    } else {
        return state;
    }
}

function loginTokenReducer(loginToken: FluxStore['loginToken'] = '', action: any = {}) {
    if (action.type === SET_LOGIN_TOKEN) {
        return action.loginToken;
    } else {
        return loginToken;
    }
}

function userSettingsReducer(state: FluxStore['userSettings'] = null, action: any = {}) {
    if (action.type === SET_USER_SETTINGS) {
        return action.userSettings;
    } else {
        return state;
    }
}

function testphaseReducer(
    testphase: FluxStore['testphase'] = {
        uwe: true,
        hp_alt: true,
        hp_gruppe: true,
        json: true,
        hp_kp2: true,
        tracking: true,
        akte: true,
        carscanner: true,
        inz: true,
        lead: true,
        individ: true,
    },
    action: any = {}
) {
    if (action.type === SET_TESTPHASE) {
        return action.testphase;
    } else {
        return testphase;
    }
}

function vertrageReducer(vertrage: FluxStore['vertrage'] = null, action: any = {}) {
    if (action.type === SET_VERTRAGE) {
        return action.vertrage;
    } else {
        return vertrage;
    }
}

function carListReducer(carList: FluxStore['carList'] = null, action: any = {}) {
    if (action.type === SET_CARLIST) {
        return action.carList;
    } else {
        return carList;
    }
}

function carlistFilterReducer(carListFilter: FluxStore['carListFilter'] = null, action: any = {}) {
    if (action.type === SET_CARLIST_FILTER) {
        return action.carListFilter;
    } else {
        return carListFilter;
    }
}

function carlistFilteredReducer(carListFiltered: FluxStore['carListFiltered'] = null, action: any = {}) {
    if (action.type === SET_CARLIST_FILTERED) {
        return action.carListFiltered;
    } else {
        return carListFiltered;
    }
}

function carlistSizeReducer(carListSize: FluxStore['carListSize'] = null, action: any = {}) {
    if (action.type === SET_CARLIST_SIZE) {
        return action.carListSize;
    } else {
        return carListSize;
    }
}

function unchangedDataReducer(unchangedData: FluxStore['unchangedData'] = false, action: any = {}) {
    if (action.type === SET_UNCHANGED_DATA) {
        return action.unchangedData;
    } else {
        return unchangedData;
    }
}

function carsChangedReducer(carsChanged: FluxStore['carsChanged'] = [], action: any = {}) {
    if (action.type === SET_CHANGED_CARS) {
        return action.carsChanged;
    } else {
        return carsChanged;
    }
}

// -----------------------------------------------------------------------------------------------------------------------------

const store = createStore(
    combineReducers({
        allVehiclesState: allVehicleStateReducer,
        app: toastReducer,
        carList: carListReducer,
        carListFilter: carlistFilterReducer,
        carListFiltered: carlistFilteredReducer,
        carListSize: carlistSizeReducer,
        carsChanged: carsChangedReducer,
        imageSettings: imageSettingsReducer,
        lastLeadListUpdate: lastLeadListUpdate,
        leadstatus: leadstatusReducer,
        loginToken: loginTokenReducer,
        updateRequired: updateRequiredReducer,
        unchangedData: unchangedDataReducer,
        userSettings: userSettingsReducer,
        testphase: testphaseReducer,
        vertrage: vertrageReducer,
    })
);

export default store;

export type FluxStore = {
    allVehiclesState: {
        filter?: string;
        suche?: Search;
        sort?: Sort;
        status?: string;
    };
    app: {
        notifications?: any[];
    };
    carList: Car[];
    carListFilter: AxiosGetCarlist['facets'];
    carListFiltered: Car['id'][];
    carListSize: {
        all?: number;
        [index: string]: number;
    };
    carsChanged: Car['id'][];
    imageSettings: FirmaBilderSettings & {
        ec?: number;
    };
    lastLeadListUpdate: number;
    leadstatus: number;
    loginToken: string;
    updateRequired: boolean;
    unchangedData: boolean;
    userSettings: UserSettings;
    testphase: Testphasen;
    vertrage: Vertrage;
};
